import React, { useCallback } from "react"
import { COLORS } from "../../utilities/Constants"
import { FieldColumn } from "../cards/EntiteCards"
import { generalStyles } from "../styles/styles"
import { PdfSlot } from "../basics/interface/AppPdfs"
import InputV2 from "../basics/input/InputV2"
import { useSelector, useDispatch } from "react-redux"
import {
	PAYE_PAR_ASSURANCE,
	PAYE_PAR_CLIENT,
	PAYE_PAR_MASTER,
} from "../atelier/AtelierLigneV2"
import {
	transactionEstFacture,
	filtrerMoyensPaiement,
} from "../../hooks/moyenPaiement"
import { useGroups } from "../../hooks/groups"
import { filterEmployesActifs } from "../../utilities/employes"
import { estPerte as estPerteUtil } from "../../utilities/transactionsV2"
import { showToast } from "../../store/actions/toastActions"
import CustomModal from "../basics/interface/CustomModal"

const TransactionV2 = ({
	transaction,
	onChange,
	onDelete,
	onDeleteDocument,
	index,
	// Motif d'une ligne figée (revue 4 location) : pas de ×, régulariser
	// par une transaction.
	suppressionBloquee = null,
	onGenerate = (id) => {},
	// Si fournie : upload immédiat de la facture à la sélection du fichier
	// (PUT côté parent) au lieu d'un simple set local persisté à la sauvegarde.
	onUploadFacture = null,
	// Fiche close : date + quittance + facture verrouillées (consultation seule).
	locked = false,
	// Filtrage moyens de paiement par module + entité (optionnels).
	module = null,
	entite = null,
	// A4 (Jordan 12.10) : (transaction, motif) → Promise<message d'erreur |
	// null>. Fourni par la fiche dépannage : annulation d'un encaissement
	// terrain (boss / admin / responsable : la fiche ne la fournit qu'à eux).
	onAnnulerEncaissement = null,
	// Jordan 15.10 (fiche dépannage) : règle de datation commune
	// {modifiable, raison} et droits par champ (Q17 : {moyen, payeur,
	// montant, caissier, details, raison, pourquoi}) — remplacent alors les
	// règles boss / admin + ligne figée ; motif de refus de l'annulation
	// terrain (bouton grisé, raison en infobulle).
	datation = null,
	champs = null,
	refusAnnulation = null,
}) => {
	const { moyenPaiements: allMoyenPaiements } = useSelector(
		(state) => state.autreInfo,
	)
	const moyenPaiements = module
		? filtrerMoyensPaiement(allMoyenPaiements, {
				module,
				entite,
				payeParSelect: transaction.payeParSelect,
				valeurCourante: transaction.moyenPaie,
			})
		: allMoyenPaiements
	const { employes: depanneurs } = useSelector((state) => state.masterReducer)
	const dispatch = useDispatch()
	const { isBoss, isAdmin } = useGroups()
	// Date + détails paiement : édition réservée boss/admin, même fiche ouverte.
	// Revue 5 : ligne figée (location) → date, moyen, montant et payeur ne
	// se modifient plus (refus serveur 409) ; caissier et libellés restent.
	const figee = !!suppressionBloquee
	// Revue 6 (D8) : figée par son SEUL moyen → elle peut encore être DATÉE
	// (encaissée) ; le serveur refuse tout autre changement.
	const datable = suppressionBloquee === "Moyen de paiement posé"
	const dateDetailsLocked = locked || !(isBoss || isAdmin)
	const set = (key, value) => onChange(index, key, value)
	const estPerte = estPerteUtil(transaction)
	// GE-60 (revue I1) : encaissement d'une couverture d'assistance — payeur,
	// moyen et montant posés par « Encaisser » (verrouillés), suppression
	// confirmée.
	const lieCouverture = !!transaction.couverture
	const [confirmSuppr, setConfirmSuppr] = React.useState(false)
	// Encaissement terrain enregistré : pas de × (le serveur ne le supprime
	// pas via la fiche) — annulation dédiée, boss / admin.
	const terrain = !!transaction.dep_mission && !!transaction.id
	// Jordan 15.10 : visible même quittancée (toujours le cas en terrain) ;
	// datée / rapprochée / facturée / WinBIZ → grisé, raison en infobulle.
	const peutAnnuler = terrain && !locked && !!onAnnulerEncaissement
	const dateDisabled = datation
		? locked || !datation.modifiable
		: dateDetailsLocked || (figee && !datable)
	// Champ figé : par la règle de la fiche si fournie, sinon l'historique.
	const fige = (cle, historique) =>
		champs ? locked || !champs[cle] : historique
	const titreFige = (cle) =>
		champs && !champs[cle] && champs.pourquoi ? champs.pourquoi : undefined
	const [annulation, setAnnulation] = React.useState(null)
	// La fiche est rechargée après l'annulation : la ligne disparaît (pas
	// de setState après démontage).
	const monte = React.useRef(true)
	React.useEffect(() => () => (monte.current = false), [])
	const confirmerAnnulation = async () => {
		if (!annulation.confirmer) {
			setAnnulation((a) => ({ ...a, confirmer: true, erreur: null }))
			return
		}
		setAnnulation((a) => ({ ...a, envoi: true }))
		const erreur = await onAnnulerEncaissement(
			transaction,
			annulation.motif.trim(),
		)
		if (!monte.current) return
		if (erreur)
			setAnnulation((a) => ({
				...a,
				envoi: false,
				confirmer: false,
				erreur,
			}))
		else setAnnulation(null)
	}

	const setPayePar = useCallback(
		(newPayePar) => {
			onChange(index, "payeParSelect", newPayePar)
			if (
				newPayePar === PAYE_PAR_CLIENT &&
				module &&
				entite &&
				entite.id > 0 &&
				entite.can_payer_par_facture === false &&
				transactionEstFacture(transaction, allMoyenPaiements)
			) {
				onChange(index, "moyenPaie", 0)
				dispatch(
					showToast(
						"warning",
						"Ce client ne peut pas payer par facture",
					),
				)
			}
		},
		[
			onChange,
			index,
			module,
			entite,
			transaction,
			allMoyenPaiements,
			dispatch,
		],
	)
	return (
		<div
			style={{
				backgroundColor: estPerte ? COLORS.perteBg : COLORS.surface,
				border: `1px solid ${estPerte ? COLORS.perteBorder : COLORS.border}`,
				borderRadius: 14,
				overflow: "hidden",
				// L'espacement entre lignes vient du `gap` de
				// TransactionsCardV2 (seul rendeur du composant).
			}}
		>
			{/* Header de la transaction */}
			<div
				style={{
					padding: "10px 16px",
					backgroundColor: estPerte
						? COLORS.perteHeaderBg
						: COLORS.bg,
					borderBottom: `1px solid ${estPerte ? COLORS.perteBorder : COLORS.border}`,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<span
					style={{
						fontSize: 12,
						fontWeight: 500,
						color: COLORS.textMuted,
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					#{index + 1}
					{lieCouverture && (
						<span
							style={{
								padding: "1px 9px",
								borderRadius: 99,
								fontSize: 11,
								fontWeight: 700,
								color: "#166534",
								backgroundColor: "#DCFCE7",
								border: "1px solid #BBF7D0",
							}}
							title="Encaissement lié à une couverture d'assistance"
						>
							Couverture
							{transaction.payeur_label
								? ` ${transaction.payeur_label}`
								: ""}
						</span>
					)}
				</span>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					{/* Encaissement terrain (re-lié à la fiche) : pas de × ;
					    annulation dédiée boss / admin (A4, Jordan 12.10). */}
					{!locked && terrain && !suppressionBloquee && (
						<span
							style={{ fontSize: 11.5, color: COLORS.textMuted }}
						>
							Encaissement terrain
						</span>
					)}
					{peutAnnuler && (
						<button
							type="button"
							disabled={!!refusAnnulation}
							onClick={() =>
								setAnnulation({
									motif: "",
									confirmer: false,
									envoi: false,
									erreur: null,
								})
							}
							style={{
								height: 28,
								padding: "0 10px",
								borderRadius: 8,
								border: `1px solid ${COLORS.dangerBorder}`,
								backgroundColor: COLORS.dangerLight,
								color: COLORS.danger,
								fontSize: 12,
								fontWeight: 600,
								cursor: refusAnnulation
									? "not-allowed"
									: "pointer",
								opacity: refusAnnulation ? 0.5 : 1,
							}}
							title={
								refusAnnulation ||
								"Annuler cet encaissement fait sur le terrain (motif obligatoire)"
							}
						>
							Annuler l'encaissement
						</button>
					)}
					{!locked && suppressionBloquee && (
						<span
							style={{ fontSize: 11.5, color: COLORS.textMuted }}
							title="Ligne figée : régulariser par une transaction, pas de suppression."
						>
							{suppressionBloquee}
						</span>
					)}
					{!locked && !suppressionBloquee && !terrain && (
						<button
							onClick={() =>
								lieCouverture
									? setConfirmSuppr(true)
									: onDelete(index)
							}
							style={{
								width: 28,
								height: 28,
								borderRadius: 8,
								border: `1px solid ${COLORS.dangerBorder}`,
								backgroundColor: COLORS.dangerLight,
								color: COLORS.danger,
								fontSize: 14,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							×
						</button>
					)}
				</div>
			</div>

			{/* Champs */}
			<div
				style={{
					padding: 16,
					display: "flex",
					flexDirection: "column",
					gap: 14,
				}}
			>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: estPerte
							? "1fr 2fr 1fr"
							: "1fr 1fr 2fr 1fr 1fr",
						gap: 12,
					}}
				>
					{!estPerte && (
						<FieldColumn label="Moy. paiement">
							<select
								value={transaction.moyenPaie || ""}
								onChange={(e) =>
									set("moyenPaie", parseInt(e.target.value))
								}
								disabled={fige(
									"moyen",
									locked || lieCouverture || figee,
								)}
								title={titreFige("moyen")}
								style={generalStyles.select}
							>
								<option value={0}>-</option>
								{moyenPaiements.map((mdp, idx) => (
									<option key={`mdp_${idx}`} value={mdp.id}>
										{mdp.nom}
									</option>
								))}
							</select>
						</FieldColumn>
					)}
					<FieldColumn label="Payé par">
						<select
							value={transaction.payeParSelect || 0}
							onChange={(e) =>
								setPayePar(parseInt(e.target.value))
							}
							disabled={fige(
								"payeur",
								locked || lieCouverture || figee,
							)}
							title={titreFige("payeur")}
							style={generalStyles.select}
						>
							<option value={-1}>-</option>
							{/* GE-60 : « Assistance » en dépannage seulement (l'atelier
							    et la location gardent « Assurance ») ; même valeur. */}
							<option value={PAYE_PAR_ASSURANCE}>
								{module === "dep" ? "Assistance" : "Assurance"}
							</option>
							<option value={PAYE_PAR_CLIENT}>Client</option>
							<option value={PAYE_PAR_MASTER}>Perte</option>
						</select>
					</FieldColumn>
					<FieldColumn
						label={estPerte ? "Raison perte" : "Raison paiement"}
					>
						<input
							type="text"
							value={transaction.raison || ""}
							onChange={(e) => set("raison", e.target.value)}
							disabled={fige("raison", locked)}
							title={titreFige("raison")}
							style={generalStyles.input}
							placeholder={
								estPerte
									? "Ex: pièce offerte, casse atelier..."
									: "Ex: Acompte, solde..."
							}
						/>
					</FieldColumn>
					{!estPerte && (
						<FieldColumn label="Caissier">
							<select
								value={transaction.caissier || ""}
								onChange={(e) =>
									set("caissier", parseInt(e.target.value))
								}
								disabled={fige("caissier", locked)}
								title={
									champs && !champs.caissier
										? champs.pourquoi ||
											"Caissier : celui qui a généré la transaction (modifiable par le boss ou l'admin)"
										: undefined
								}
								style={generalStyles.select}
							>
								<option>-</option>
								{filterEmployesActifs(
									depanneurs,
									transaction.caissier,
								).map((depa, i) => (
									<option
										key={`caissier_${i}`}
										value={depa.id}
									>
										{depa.nom}
									</option>
								))}
							</select>
						</FieldColumn>
					)}
					<FieldColumn label="Tarif">
						<input
							type="number"
							value={transaction.total || ""}
							onChange={(e) => set("total", e.target.value)}
							disabled={fige(
								"montant",
								locked || lieCouverture || figee,
							)}
							title={titreFige("montant")}
							style={{
								...generalStyles.input,
								textAlign: "right",
							}}
							placeholder="0.00"
						/>
					</FieldColumn>
				</div>

				{!estPerte && (
					<>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "320px 1fr",
								gap: 12,
							}}
						>
							<FieldColumn label="Date">
								<div
									title={
										(datation && datation.raison) ||
										undefined
									}
								>
									<InputV2
										type="date"
										value={transaction.date || ""}
										onChange={(e) =>
											set("date", e.target.value)
										}
										disabled={dateDisabled}
										style={generalStyles.input}
									/>
								</div>
							</FieldColumn>
							<FieldColumn label="Détails paiement">
								<input
									type="text"
									// Miroir de TransactionV2.details en base : sans ça, la
									// saisie trop longue ne se voit qu'au save, en 500.
									maxLength={200}
									value={transaction.details || ""}
									onChange={(e) =>
										set("details", e.target.value)
									}
									disabled={fige(
										"details",
										dateDetailsLocked,
									)}
									style={generalStyles.input}
									placeholder="Informations complémentaires..."
								/>
							</FieldColumn>
						</div>

						{/* Documents */}
						{Number.isInteger(transaction.id) && (
							<div
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: 16,
								}}
							>
								<PdfSlot
									label="Quittance"
									doc={transaction.quittance || null}
									onUpload={(normalizedPdf) => {
										set("quittance", normalizedPdf)
									}}
									onDelete={() =>
										onDeleteDocument(
											transaction.id,
											"quittance",
										)
									}
									onGenerate={() =>
										onGenerate(transaction.id)
									}
									isUpload={false}
									readOnly={locked}
								/>
								{transactionEstFacture(
									transaction,
									moyenPaiements,
								) && (
									<PdfSlot
										label="Facture"
										doc={transaction.facture || null}
										onUpload={(normalizedPdf) => {
											if (onUploadFacture)
												onUploadFacture(
													transaction.id,
													normalizedPdf.file,
												)
											else set("facture", normalizedPdf)
										}}
										onDelete={() =>
											onDeleteDocument(
												transaction.id,
												"facture",
											)
										}
										readOnly={locked}
									/>
								)}
							</div>
						)}
						{/* Retour client 15.10 : une ligne ajoutée n'existe qu'en
						local jusqu'à l'enregistrement de la fiche — pas de
						créneau Quittance avant, on le dit. */}
						{!Number.isInteger(transaction.id) && !locked && (
							<div
								style={{
									fontSize: 11.5,
									color: COLORS.textMuted,
								}}
							>
								Enregistrez la fiche pour générer la quittance.
							</div>
						)}
					</>
				)}

				{transaction.extra_data?.winbiz && (
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: 6,
							backgroundColor: COLORS.successLight,
							border: `1px solid ${COLORS.border}`,
							borderRadius: 8,
							padding: "4px 10px",
							fontSize: 12,
							color: COLORS.success,
							fontWeight: 500,
						}}
					>
						WinBIZ
						{transaction.extra_data.winbiz.numero
							? ` n° ${transaction.extra_data.winbiz.numero}`
							: ""}
						{transaction.extra_data.winbiz.manuel && (
							<span
								style={{
									color: COLORS.textMuted,
									fontWeight: 400,
									marginLeft: 4,
								}}
							>
								(saisie manuelle)
							</span>
						)}
					</div>
				)}
			</div>
			{annulation && (
				<CustomModal
					title="Annuler l'encaissement terrain ?"
					subtitle={`M${transaction.dep_mission} · ${
						transaction.payeur_label || "Client"
					} · ${transaction.total}.–`}
					onClose={() => !annulation.envoi && setAnnulation(null)}
					maxWidth={520}
					buttons={[
						{
							title: "Fermer",
							styleName: "secondary",
							onClick: () => setAnnulation(null),
							disabled: annulation.envoi,
						},
						{
							title: annulation.confirmer
								? "Confirmer ?"
								: "Annuler l'encaissement",
							styleName: "danger",
							onClick: confirmerAnnulation,
							disabled:
								annulation.envoi || !annulation.motif.trim(),
						},
					]}
				>
					<div style={{ display: "grid", gap: 10, fontSize: 13.5 }}>
						<div>
							La ligne est supprimée et une note (non imprimée)
							garde la trace sur la fiche.
							{lieCouverture
								? " Le montant redevient disponible sur la couverture."
								: " Le reste à payer est recalculé."}
						</div>
						<textarea
							value={annulation.motif}
							onChange={(e) =>
								setAnnulation({
									...annulation,
									motif: e.target.value,
									confirmer: false,
								})
							}
							placeholder="Motif (obligatoire)"
							rows={3}
							style={{
								...generalStyles.input,
								height: "auto",
								resize: "vertical",
							}}
						/>
						{annulation.erreur && (
							<div style={{ color: COLORS.danger }}>
								{annulation.erreur}
							</div>
						)}
					</div>
				</CustomModal>
			)}
			{confirmSuppr && (
				<CustomModal
					title="Supprimer l'encaissement de la couverture ?"
					subtitle={`${transaction.payeur_label || "Assistance"} · ${
						transaction.total
					}.–`}
					onClose={() => setConfirmSuppr(false)}
					maxWidth={520}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmSuppr(false),
						},
						{
							title: "Supprimer",
							styleName: "danger",
							onClick: () => {
								setConfirmSuppr(false)
								onDelete(index)
							},
						},
					]}
				>
					<div style={{ fontSize: 13.5 }}>
						L'encaissement sera retiré à la sauvegarde de la fiche ;
						le montant redevient disponible sur la couverture.
					</div>
				</CustomModal>
			)}
		</div>
	)
}

export default TransactionV2
